import { useViewSize, ViewSize } from '@susu/custom-made/hooks/useViewSize';
import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

declare global {
  interface WindowEventMap {
    tooltipOpened: CustomEvent;
  }
}

export function useTooltip(
  tooltipRef: RefObject<HTMLDivElement>,
  pointerRef: RefObject<HTMLDivElement>,
  tooltipId?: string,
  noResizeClosing?: boolean,
  closeCallbackFC?: () => void,
) {
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [top, setTop] = useState<number>(0);
  const [left, setLeft] = useState<number>(0);
  const [pointerTop, setPointerTop] = useState<number>(0);
  const [pointerLeft, setPointerLeft] = useState<number>(0);
  const [initiatorCoordinates, setInitiatorCoordinates] = useState<{
    topCoordinate: number;
    leftCoordinate: number;
    width: number;
  }>({ topCoordinate: 0, leftCoordinate: 0, width: 0 });
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const viewSize = useViewSize();
  const tooltipSideMargin = viewSize === ViewSize.Mobile ? 8 : 16;
  const tooltipTopMargin = viewSize === ViewSize.Mobile ? 14 : 18;

  const openTooltipHandler = (e: CustomEvent) => {
    // Only respond to events for this specific tooltip ID
    if (tooltipId && e.detail.tooltipId && e.detail.tooltipId !== tooltipId) {
      return;
    }

    const { title, description, topPosition, leftPosition, width } = e.detail;

    setTitle(title);
    setDescription(description);
    setInitiatorCoordinates({
      topCoordinate: topPosition,
      leftCoordinate: leftPosition,
      width: width,
    });
  };

  const closeTooltipHandler = () => {
    setIsOpen(false);
    setTitle('');
    setDescription('');
    setTop(0);
    setLeft(0);

    if (closeCallbackFC) {
      closeCallbackFC();
    }
  };

  useEffect(() => {
    window.addEventListener('tooltipOpened', openTooltipHandler);

    if (!noResizeClosing) {
      window.addEventListener('resize', closeTooltipHandler);
    }

    return () => {
      window.removeEventListener('tooltipOpened', openTooltipHandler);
      
      if (!noResizeClosing) {
        window.removeEventListener('resize', closeTooltipHandler);
      }
    };
  }, []);

  const setLeftPositionInsideViewport = useCallback(
    (tooltipWidth: number, pointerWidth: number) => {
      if (pointerRef.current && tooltipRef.current) {
        const screenWidth = window.innerWidth;

        const leftPosition = initiatorCoordinates.leftCoordinate + initiatorCoordinates.width / 2 - tooltipWidth / 2;
        const arrowSpace = 5;
        setPointerLeft(initiatorCoordinates.leftCoordinate + initiatorCoordinates.width / 2 - pointerWidth / 2);
        if (initiatorCoordinates.leftCoordinate - arrowSpace < tooltipSideMargin) {
          // if tooltip left space is smaller than side margin
          setLeft(initiatorCoordinates.leftCoordinate - arrowSpace); // then set same as initiator left
        } else if (leftPosition < tooltipSideMargin) {
          // if tooltip left from middle smaller than side margin
          setLeft(tooltipSideMargin); // then set side margin
        } else if (
          initiatorCoordinates.leftCoordinate +
            initiatorCoordinates.width / 2 +
            pointerWidth / 2 +
            arrowSpace +
            tooltipSideMargin >=
          screenWidth
        ) {
          // if tooltip end position bigger than screen size
          setLeft(
            initiatorCoordinates.leftCoordinate +
              initiatorCoordinates.width / 2 +
              pointerWidth / 2 +
              arrowSpace -
              tooltipWidth,
          ); // then set according the last position or arrow
        } else if (
          initiatorCoordinates.leftCoordinate + tooltipWidth + tooltipSideMargin >= screenWidth &&
          initiatorCoordinates.leftCoordinate + initiatorCoordinates.width / 2 + tooltipWidth / 2 + tooltipSideMargin >=
            screenWidth
        ) {
          // if tooltip end position from middle bigger than screen size
          setLeft(screenWidth - tooltipWidth - tooltipSideMargin); // then set end position as side margin
        } else {
          setLeft(leftPosition); // set in the middle
        }
      }
    },
    [tooltipSideMargin, initiatorCoordinates.leftCoordinate],
  );

  useEffect(() => {
    if (tooltipRef.current && pointerRef.current && (title || description)) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const pointerRect = pointerRef.current.getBoundingClientRect();
      const topPosition = initiatorCoordinates.topCoordinate - tooltipRect.height - tooltipTopMargin;
      setLeftPositionInsideViewport(tooltipRect.width, pointerRect.width);

      setTop(topPosition);
      setPointerTop(initiatorCoordinates.topCoordinate - tooltipTopMargin);
      setIsOpen(true);
    }
  }, [
    title,
    description,
    initiatorCoordinates.leftCoordinate,
    initiatorCoordinates.topCoordinate,
    setLeftPositionInsideViewport,
    tooltipTopMargin,
    tooltipRef,
  ]);

  return {
    title,
    description,
    top,
    left,
    isOpen,
    closeTooltipHandler,
    pointerTop,
    pointerLeft,
  };
}
